import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import { MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";

// Phone preview styles for SMS
const phoneStyles = {
  container: {
    position: "relative",
    margin: "0 auto",
    border: "10px solid #1A1A1A",
    borderRadius: "55px",
    height: "700px",
    width: "340px",
    background: "#000",
    boxShadow:
      "0 0 20px rgba(0, 0, 0, 0.2), inset 0 0 10px rgba(255, 255, 255, 0.1)",
    overflow: "hidden",
  },
  dynamicIsland: {
    position: "absolute",
    top: "12px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "120px",
    height: "25px",
    background: "#000",
    borderRadius: "20px",
    zIndex: 10,
  },
  screen: {
    height: "100%",
    background: "#fff",
    position: "relative",
    overflow: "auto",
    borderRadius: "40px",
    maxHeight: "700px",
  },
  statusBar: {
    position: "absolute",
    top: "5px",
    left: 0,
    right: 0,
    padding: "15px",
    display: "flex",
    justifyContent: "space-between",
    color: "#000",
    fontSize: "14px",
    zIndex: 5,
  },
  smsHeader: {
    background: "#007AFF",
    color: "#fff",
    padding: "60px 16px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  chatBackground: {
    background: "#F2F2F7",
    height: "calc(100%)",
    padding: "16px",
    minHeight: "120px",
  },
  messageBubble: {
    maxWidth: "85%",
    marginLeft: "auto",
    background: "#007AFF",
    borderRadius: "18px",
    padding: "12px 16px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    marginBottom: "8px",
    color: "#fff",
  },
  receivedMessageBubble: {
    maxWidth: "85%",
    marginRight: "auto",
    marginLeft: "0",
    background: "#E9E9EB",
    borderRadius: "18px",
    padding: "12px 16px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    marginBottom: "8px",
    color: "#000",
  },
  messageTime: {
    fontSize: "11px",
    opacity: 0.7,
    marginTop: "4px",
    textAlign: "right",
  },
};

export default function TextTemplatePreview({ id, isViewMode }) {
  const [templateName, setTemplateName] = useState("");
  const [templateFormat, setTemplateFormat] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (id) {
      fetchTemplateDetails();
    }
  }, [id]);

  const fetchTemplateDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BASE_URL}admin/getUserTextTemplate/${id}`,
        {
          headers: {
            "x-access-token": token,
            version: "1.0.0",
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.success) {
        const template = response.data.data;
        setTemplateName(template.templateName || "");
        setTemplateFormat(template.templateFormat || "");
      } else {
        toast.error(response.data.message || "Failed to fetch template details");
      }
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("Failed to fetch template details");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
        <Typography>Loading template...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "20px", width: "100%", flexWrap: "wrap", overflowX: "auto", minWidth: "320px" }}>
      {/* Left Side - Template Details */}
      <Card sx={{ flex: { xs: "100%", md: 1 }, boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", minWidth: { xs: "100%", md: "auto" } }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Text Template Details
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Template Name
            </Typography>
            <Typography variant="body1">
              {templateName || "No template name"}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Template Content
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", backgroundColor: "#f5f5f5", padding: 2, borderRadius: 1 }}>
              {templateFormat || "No template content"}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Character Count
            </Typography>
            <Typography variant="body2">
              {templateFormat ? templateFormat.length : 0} characters
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Word Count
            </Typography>
            <Typography variant="body2">
              {templateFormat ? templateFormat.trim().split(/\s+/).filter(Boolean).length : 0} words
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Message Count
            </Typography>
            <Typography variant="body2">
              {templateFormat ? Math.ceil(templateFormat.length / 160) : 0} message(s)
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      {/* Right Side - Preview */}
      <Card sx={{ flex: 1, height: "100%", boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
              Preview
            </Typography>
            {isViewMode && (
              <Typography variant="caption" color="text.secondary">
                View Mode
              </Typography>
            )}
          </Box>
          <SMSPreview messageText={templateFormat} showInput={false} />
        </CardContent>
      </Card>
    </Box>
  );
}

// SMS Preview Component (inline for simplicity)
function SMSPreview({ messageText, recipientName, showInput = true }) {
  return (
    <div style={phoneStyles.container}>
      {/* Dynamic Island (iPhone 14+ style) */}
      <div style={phoneStyles.dynamicIsland}></div>
      {/* Phone Screen */}
      <div style={phoneStyles.screen}>
        {/* Status Bar */}
        <div style={phoneStyles.statusBar}>
          <span>9:41</span>
          <span>100%</span>
        </div>
        {/* SMS Header */}
        <div style={phoneStyles.smsHeader}>
          <MessageSquare size={20} />
          <div>
            <Typography variant="subtitle2">
              {recipientName || "Messages"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              SMS Preview
            </Typography>
          </div>
        </div>
        {/* Chat Background */}
        <div style={phoneStyles.chatBackground}>
          {/* Sample received message */}
          <div style={phoneStyles.receivedMessageBubble}>
            <Typography variant="body2">
              Hey! How are you doing?
            </Typography>
            <div style={{ ...phoneStyles.messageTime, color: "#8E8E93" }}>
              2:30 PM
            </div>
          </div>
          {/* Your SMS message (sent) */}
          <div style={phoneStyles.messageBubble}>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {messageText || "Your SMS message will appear here"}
            </Typography>
            <div style={phoneStyles.messageTime}>
              2:32 PM
            </div>
          </div>
        </div>
        {/* Input Area (optional) */}
        {showInput && (
          <div style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#F2F2F7",
            padding: "12px 16px",
            borderTop: "1px solid #C6C6C8",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}>
            <input 
              type="text" 
              placeholder="Text Message" 
              style={{
                flex: 1,
                background: "#fff",
                border: "1px solid #C6C6C8",
                borderRadius: "20px",
                padding: "8px 16px",
                outline: "none",
                fontSize: "16px",
              }}
            />
            <button style={{
              background: "#007AFF",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}>
              ↑
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
